import { Calendar, CheckCircle2, Send, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ActionItemKind, ActionItemRow, ActionItemStatus } from "@/hooks/use-action-items";
import { usePatchActionItem } from "@/hooks/use-action-items";
import { cn } from "@/lib/utils";

const KIND_LABELS: Record<ActionItemKind, string> = {
  action: "Action",
  schedule: "Schedule",
  followup: "Follow-up",
};

const KIND_TINTS: Record<ActionItemKind, string> = {
  action: "bg-info-bg text-info",
  schedule: "bg-[#F1ECFB] text-[#5E3FB0] dark:bg-[#1a0e2e] dark:text-[#b89df0]",
  followup: "bg-warning-bg text-warning",
};

const STATUS_LABELS: Record<ActionItemStatus, string> = {
  pending: "Pending",
  sent: "Sent",
  snoozed: "Snoozed",
  dismissed: "Dismissed",
  done: "Done",
};

interface FollowupsListProps {
  solutionId: string;
  items: ActionItemRow[];
  filter: ActionItemStatus | "all";
  view: "row" | "tile";
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onOpenItem: (item: ActionItemRow) => void;
}

export function FollowupsList({
  solutionId,
  items,
  filter,
  view,
  selectedIds,
  onToggleSelect,
  onOpenItem,
}: FollowupsListProps) {
  const patch = usePatchActionItem(solutionId);

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  if (filtered.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--color-border)] p-12 text-center">
        <h2 className="font-display text-lg font-semibold mb-1">Nothing here</h2>
        <p className="text-sm text-muted-foreground">No action items match this filter.</p>
      </div>
    );
  }

  if (view === "row") {
    return (
      <div className="rounded-lg border border-[var(--color-border)] bg-card overflow-hidden">
        <div className="grid grid-cols-[28px_92px_1fr_110px_140px_108px_140px] gap-3 items-center px-3 py-2 border-b border-[var(--color-border)] text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
          <span aria-hidden />
          <span>Kind</span>
          <span>Title</span>
          <span>Due</span>
          <span>Owner</span>
          <span>Status</span>
          <span className="text-right">Actions</span>
        </div>
        <ul>
          {filtered.map((item) => (
            <li key={item.id}>
              <RowItem
                item={item}
                selected={selectedIds.has(item.id)}
                onToggle={() => onToggleSelect(item.id)}
                onOpen={() => onOpenItem(item)}
                onPatch={(p) => patch.mutate({ id: item.id, patch: p })}
                pending={patch.isPending}
              />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {filtered.map((item) => (
        <li key={item.id}>
          <TileItem
            item={item}
            selected={selectedIds.has(item.id)}
            onToggle={() => onToggleSelect(item.id)}
            onOpen={() => onOpenItem(item)}
            onPatch={(p) => patch.mutate({ id: item.id, patch: p })}
            pending={patch.isPending}
          />
        </li>
      ))}
    </ul>
  );
}

// ── Row ───────────────────────────────────────────────────────────

function RowItem({
  item,
  selected,
  onToggle,
  onOpen,
  onPatch,
  pending,
}: {
  item: ActionItemRow;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onPatch: (p: { status?: ActionItemStatus }) => void;
  pending: boolean;
}) {
  const dim = item.status === "dismissed" || item.status === "done";
  return (
    <div
      className={cn(
        "grid grid-cols-[28px_92px_1fr_110px_140px_108px_140px] gap-3 items-center px-3 py-2 border-b border-[var(--color-border)] last:border-b-0 transition-opacity",
        dim && "opacity-60",
      )}
    >
      <Checkbox checked={selected} onCheckedChange={onToggle} aria-label="Select item" />
      <span
        className={cn(
          "inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-semibold",
          KIND_TINTS[item.kind],
        )}
      >
        {KIND_LABELS[item.kind]}
      </span>
      <button
        type="button"
        onClick={onOpen}
        className="text-sm text-foreground/90 truncate text-left hover:text-nymbl-blue hover:underline"
        title={item.title}
      >
        {item.title}
      </button>
      <span className="text-xs text-muted-foreground tabular-nums">
        {item.dueDate ? new Date(item.dueDate).toLocaleDateString() : "—"}
      </span>
      <span className="text-xs text-muted-foreground truncate inline-flex items-center gap-1.5">
        {item.sourceSpeaker?.initials && (
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-nymbl-navy text-white text-[8px] font-semibold shrink-0">
            {item.sourceSpeaker.initials}
          </span>
        )}
        <span className="truncate">{item.sourceSpeaker?.name ?? "—"}</span>
      </span>
      <Select
        value={item.status}
        onValueChange={(value) => onPatch({ status: value as ActionItemStatus })}
      >
        <SelectTrigger className="h-7 w-full text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(STATUS_LABELS) as ActionItemStatus[]).map((s) => (
            <SelectItem key={s} value={s}>
              {STATUS_LABELS[s]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex items-center justify-end gap-1">
        {item.status !== "sent" && item.status !== "done" && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onPatch({ status: "sent" })}
            disabled={pending}
            className="h-7 px-2 text-xs"
            title="Push to Deliver"
          >
            <Send className="h-3 w-3 mr-1" />
            Push
          </Button>
        )}
        {item.status === "sent" && (
          <span className="text-[10px] uppercase tracking-[0.14em] text-success font-semibold inline-flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Sent
          </span>
        )}
      </div>
    </div>
  );
}

// ── Tile (existing) ───────────────────────────────────────────────

function TileItem({
  item,
  selected,
  onToggle,
  onOpen,
  onPatch,
  pending,
}: {
  item: ActionItemRow;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onPatch: (p: { status?: ActionItemStatus }) => void;
  pending: boolean;
}) {
  const dim = item.status === "dismissed" || item.status === "done";
  return (
    <article
      className={cn(
        "rounded-lg border border-[var(--color-border)] bg-card p-4 transition-opacity",
        dim && "opacity-60",
        selected && "ring-2 ring-nymbl-blue",
      )}
    >
      <div className="flex items-center gap-2 mb-1">
        <Checkbox checked={selected} onCheckedChange={onToggle} aria-label="Select item" />
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium",
            KIND_TINTS[item.kind],
          )}
        >
          {KIND_LABELS[item.kind]}
        </span>
        {item.source?.name && (
          <span className="text-[11px] text-muted-foreground truncate">
            From {item.source.name}
          </span>
        )}
        <Select
          value={item.status}
          onValueChange={(value) => onPatch({ status: value as ActionItemStatus })}
        >
          <SelectTrigger className="h-7 ml-auto w-[110px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(STATUS_LABELS) as ActionItemStatus[]).map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <button
        type="button"
        onClick={onOpen}
        className="block w-full text-left font-display text-sm font-semibold leading-snug text-foreground hover:text-nymbl-blue hover:underline"
      >
        {item.title}
      </button>
      {item.quote && (
        <p className="text-xs text-muted-foreground italic mt-1 line-clamp-2">"{item.quote}"</p>
      )}
      <div className="flex items-center gap-3 mt-2.5 text-xs text-muted-foreground">
        {item.sourceSpeaker && (
          <span className="inline-flex items-center gap-1">
            <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-nymbl-navy text-white text-[8px] font-semibold">
              {item.sourceSpeaker.initials}
            </span>
            {item.sourceSpeaker.name}
          </span>
        )}
        {item.dueDate && (
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            Due {new Date(item.dueDate).toLocaleDateString()}
          </span>
        )}
        {item.pmTarget && (
          <span className="text-[11px]">
            PM: {item.pmTarget.project} · {item.pmTarget.type}
            {item.pmTarget.epic && ` · ${item.pmTarget.epic}`}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {item.status !== "done" && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onPatch({ status: "done" })}
              disabled={pending}
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
              Done
            </Button>
          )}
          {item.status !== "sent" && item.status !== "done" && (
            <Button
              size="sm"
              onClick={() => onPatch({ status: "sent" })}
              disabled={pending}
              title="Push to Deliver"
            >
              <Send className="h-3.5 w-3.5 mr-1" />
              Push to Deliver
            </Button>
          )}
          {item.status !== "dismissed" && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onPatch({ status: "dismissed" })}
              disabled={pending}
            >
              <XCircle className="h-3.5 w-3.5 mr-1" />
              Dismiss
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}
